import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  ChevronRight,
  Eye,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { useClauseStatuses } from '@/hooks/use-clause-status';
import { useComplianceProfile } from '@/hooks/use-compliance-profile';
import { useComplianceSections } from '@/hooks/use-compliance-sections';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import type {
  ClauseStatusDetail,
  ComplianceSectionTaxonomy,
  SectionSummary,
} from '@/lib/types';

const SECTION_PROFILE_FILTERS: Record<number, 'trainer_or_venue'> = {
  6: 'trainer_or_venue',
};

function getSectionNumber(clauseRef: string): number {
  const match = clauseRef.match(/^(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

function buildSectionSummaries(
  sections: ComplianceSectionTaxonomy[],
  clauses: ClauseStatusDetail[],
): Map<number, SectionSummary> {
  const map = new Map<number, SectionSummary>();

  for (const section of sections) {
    map.set(section.number, {
      section_number: section.number,
      section_title: section.name,
      total_clauses: 0,
      applicable_clauses: 0,
      compliant: 0,
      non_compliant: 0,
      partially_compliant: 0,
      not_assessed: 0,
      manual_review: 0,
      last_assessed_at: null,
    });
  }

  for (const clause of clauses) {
    const sn = getSectionNumber(clause.clause_ref);
    const summary = map.get(sn);
    if (!summary) continue;

    summary.total_clauses++;
    if (clause.status === 'not_applicable') continue;
    summary.applicable_clauses++;

    if (clause.status === 'compliant') summary.compliant++;
    else if (clause.status === 'non_compliant') summary.non_compliant++;
    else if (clause.status === 'partially_compliant') summary.partially_compliant++;
    else if (clause.status === 'not_assessed') {
      summary.not_assessed++;
      if (!clause.auto_collectible) summary.manual_review++;
    }

    if (
      clause.assessed_at &&
      (!summary.last_assessed_at || clause.assessed_at > summary.last_assessed_at)
    ) {
      summary.last_assessed_at = clause.assessed_at;
    }
  }

  return map;
}

function StatusBar({ summary }: { summary: SectionSummary }) {
  const total = summary.applicable_clauses;
  if (total === 0) return null;

  const segments = [
    { count: summary.compliant, color: 'bg-green-500' },
    { count: summary.partially_compliant, color: 'bg-amber-500' },
    { count: summary.non_compliant, color: 'bg-red-500' },
    { count: summary.not_assessed - summary.manual_review, color: 'bg-gray-300' },
    { count: summary.manual_review, color: 'bg-violet-300' },
  ];

  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100">
      {segments.map((seg, i) =>
        seg.count > 0 ? (
          <div
            key={i}
            className={`${seg.color} transition-all`}
            style={{ width: `${(seg.count / total) * 100}%` }}
          />
        ) : null,
      )}
    </div>
  );
}

function SectionCard({ summary }: { summary: SectionSummary }) {
  const timeZone = useOrgTimeZone();
  return (
    <Link to={`/compliance/clauses/section/${summary.section_number}`}>
      <Card className="hover:shadow-md transition-shadow cursor-pointer">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-base">
            <span>
              {summary.section_number}. {summary.section_title}
            </span>
            <ChevronRight className="h-4 w-4 text-gray-400" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <StatusBar summary={summary} />

          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-1.5 text-gray-600">
              <Shield className="h-3.5 w-3.5" />
              <span>{summary.applicable_clauses} clauses</span>
            </div>
            <div className="flex items-center gap-1.5 text-green-600">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span>{summary.compliant} compliant</span>
            </div>
            <div className="flex items-center gap-1.5 text-red-600">
              <XCircle className="h-3.5 w-3.5" />
              <span>{summary.non_compliant} non-compliant</span>
            </div>
            {summary.partially_compliant > 0 && (
              <div className="flex items-center gap-1.5 text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span>{summary.partially_compliant} partial</span>
              </div>
            )}
            {summary.manual_review > 0 && (
              <div className="flex items-center gap-1.5 text-violet-600">
                <Eye className="h-3.5 w-3.5" />
                <span>{summary.manual_review} manual review</span>
              </div>
            )}
          </div>

          {summary.last_assessed_at ? (
            <p className="flex items-center gap-1 text-xs text-gray-500">
              <Clock className="h-3 w-3" />
              Last assessed{' '}
              {formatDateTimeInTimeZone(summary.last_assessed_at, timeZone, { dateStyle: 'medium' })}
            </p>
          ) : (
            <p className="text-xs text-gray-400">Not yet assessed</p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

export function ClausesLandingPage() {
  const { data: clauses, isLoading, error } = useClauseStatuses();
  const { data: profile } = useComplianceProfile();
  const {
    data: sections,
    isLoading: sectionsLoading,
    error: sectionsError,
  } = useComplianceSections();

  const visibleSections = useMemo(() => {
    if (!sections) return [];
    return sections.filter((section) => {
      const conditionalOn = SECTION_PROFILE_FILTERS[section.number];
      if (conditionalOn === 'trainer_or_venue') {
        if (!profile) return false;
        return profile.is_trainer || profile.operates_venues;
      }
      return true;
    });
  }, [profile, sections]);

  const summaries = useMemo(() => {
    if (!sections || !clauses) return new Map();
    return buildSectionSummaries(sections, clauses);
  }, [clauses, sections]);

  if (isLoading || sectionsLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 rounded bg-gray-200" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-48 rounded-lg bg-gray-100" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || sectionsError) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load clause data. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Clause Overview</h1>
        <p className="mt-1 text-sm text-gray-500">
          FM-312 audit structure — Sections 2–8
        </p>
      </div>

      {visibleSections.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
          <Shield className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No sections available
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Configure your compliance profile to see applicable sections.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visibleSections.map((section) => {
            const summary = summaries.get(section.number);
            if (!summary) return null;
            return <SectionCard key={section.number} summary={summary} />;
          })}
        </div>
      )}
    </div>
  );
}
